import React, { useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import i18n from './i18n/i18n';

// Auth
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProfileProvider, useProfile } from './context/ProfileContext';
import { ServiceAccessProvider, useServiceAccess } from './context/ServiceAccessContext';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import RoleRoute from './components/RoleRoute/RoleRoute';
import ServiceRoute from './components/ServiceRoute/ServiceRoute';
import ServiceAccessGate from './components/ServiceRoute/ServiceAccessGate';
import RoleBasedHome from './components/RoleBasedHome/RoleBasedHome';
import MaintenanceGate from './components/MaintenanceGate/MaintenanceGate';

// Components
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import AIChatbot from './components/AIChatbot/AIChatbot';
import CustomerSupportChat from './components/CustomerSupportChat/CustomerSupportChat';
import QuickAddFAB from './components/QuickAddFAB/QuickAddFAB';
import ProfileSwitcher from './components/ProfileSwitcher/ProfileSwitcher';
import ProfileBanner from './components/ProfileBanner/ProfileBanner';
import AnnouncementMarquee from './components/AnnouncementMarquee/AnnouncementMarquee';
import ConsentModal from './components/ConsentModal/ConsentModal';
import { ServiceWalletProvider } from './components/ServiceWallet/WalletContext';
import InactivityWarning from './components/InactivityWarning/InactivityWarning';
import CookieConsent from './components/CookieConsent/CookieConsent';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';

// Auth Pages
import Login from './pages/Login/Login';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import VerifyEmail from './pages/VerifyEmail/VerifyEmail';

// Pages
import Home from './pages/Home/Home';
import AnnouncementsList from './pages/Announcements/AnnouncementsList';
import HealthRecords from './pages/HealthRecords/HealthRecords';
import HealthAssessment from './pages/HealthAssessment/HealthAssessment';
import HealthSummary from './pages/HealthSummary/HealthSummary';
import MedicineReminder from './pages/MedicineReminder/MedicineReminder';
import Profile from './pages/Profile/Profile';
import AddressBook from './pages/AddressBook/AddressBook';
import MyPayments from './pages/MyPayments/MyPayments';
import MyBookings from './pages/MyBookings/MyBookings';
import GymServices from './pages/GymServices/GymServices';
import TeleConsultation from './pages/TeleConsultation/TeleConsultation';
import ChildDevelopment from './pages/ChildDevelopment/ChildDevelopment';
import ElderlyCare from './pages/ElderlyCare/ElderlyCare';
import InsuranceRecords from './pages/InsuranceRecords/InsuranceRecords';
import InsuranceRecord from './pages/InsuranceRecords/InsuranceRecord';

import Reminders from './pages/Reminders/Reminders';
import WearableDevices from './pages/WearableDevices/WearableDevices';
import SponsoredPackages from './pages/SponsoredPackages/SponsoredPackages';
import SymptomsChecker from './pages/SymptomsChecker/SymptomsChecker';
import SmartReports from './pages/SmartReports/SmartReports';
import DiseasePrediction from './pages/DiseasePrediction/DiseasePrediction';
import WomensHealth from './pages/WomensHealth/WomensHealth';
import CycleTracker from './pages/WomensHealth/CycleTracker/CycleTracker';
import DailyLog from './pages/WomensHealth/DailyLog/DailyLog';
import Education from './pages/WomensHealth/Education/Education';
import Preconception from './pages/WomensHealth/Preconception/Preconception';
import PregnancyDashboard from './pages/WomensHealth/PregnancyDashboard/PregnancyDashboard';
import PregnancyHealth from './pages/WomensHealth/PregnancyHealth/PregnancyHealth';
import PregnancyDocs from './pages/WomensHealth/PregnancyDocs/PregnancyDocs';
import PostpartumRecovery from './pages/WomensHealth/PostpartumRecovery/PostpartumRecovery';
import PostpartumMood from './pages/WomensHealth/PostpartumMood/PostpartumMood';
import Feeding from './pages/WomensHealth/Feeding/Feeding';
import PostpartumFitness from './pages/WomensHealth/PostpartumFitness/PostpartumFitness';
import MenopauseHub from './pages/WomensHealth/MenopauseHub/MenopauseHub';

// Mental Wellness Pages
import MentalWellness from './pages/MentalWellness/MentalWellness';
import AIFamilyDoctor from './pages/AIFamilyDoctor/AIFamilyDoctor';
import MoodCheckin from './pages/MentalWellness/MoodCheckin/MoodCheckin';
import BreathingExercise from './pages/MentalWellness/Breathing/BreathingExercise';
import ScreeningAssessment from './pages/MentalWellness/Screening/ScreeningAssessment';
import SelfCareActionPlan from './pages/MentalWellness/ActionPlan/SelfCareActionPlan';
import AdminWellnessCalendar from './pages/Admin/AdminWellnessCalendar';

// HR Portal Pages (lazy loaded)
const HRDashboard = React.lazy(() => import('./pages/HR/HRDashboard'));
const HREmployees = React.lazy(() => import('./pages/HR/HREmployees'));
const HRBulkUpload = React.lazy(() => import('./pages/HR/HRBulkUpload'));
const HRApprovals = React.lazy(() => import('./pages/HR/HRApprovals'));
const HRPlans = React.lazy(() => import('./pages/HR/HRPlans'));
const HRServices = React.lazy(() => import('./pages/HR/HRServices'));
const HRInvoices = React.lazy(() => import('./pages/HR/HRInvoices'));
const HRMediclaim = React.lazy(() => import('./pages/HR/HRMediclaim'));
const HRAnnouncements = React.lazy(() => import('./pages/HR/HRAnnouncements'));
const EmployeeMediclaim = React.lazy(() => import('./pages/Employee/EmployeeMediclaim'));

// Admin Pages (lazy loaded)
const AdminCorporates = React.lazy(() => import('./pages/Admin/AdminCorporates'));
const AdminCreateCorporate = React.lazy(() => import('./pages/Admin/AdminCreateCorporate'));
const AdminCorporateDetail = React.lazy(() => import('./pages/Admin/AdminCorporateDetail'));
const AdminVouchers = React.lazy(() => import('./pages/Admin/AdminVouchers'));
const AdminVendorForm = React.lazy(() => import('./pages/Admin/AdminVendorForm'));
const AdminPackages = React.lazy(() => import('./pages/Admin/AdminPackages'));
const AdminPlatformServices = React.lazy(() => import('./pages/Admin/AdminPlatformServices'));
const AdminBilling = React.lazy(() => import('./pages/Admin/AdminBilling'));
const AdminAIUsage = React.lazy(() => import('./pages/Admin/AdminAIUsage'));
const AdminMessaging = React.lazy(() => import('./pages/Admin/AdminMessaging'));
const AdminGymServices = React.lazy(() => import('./pages/Admin/AdminGymServices'));
const AdminTeleConsultation = React.lazy(() => import('./pages/Admin/AdminTeleConsultation'));
const AdminWomensHealthContent = React.lazy(() => import('./pages/Admin/AdminWomensHealthContent'));
const AdminElderlyCareContent = React.lazy(() => import('./pages/Admin/AdminElderlyCareContent'));
const AdminCSTeam = React.lazy(() => import('./pages/Admin/AdminCSTeam'));
const AdminRoles = React.lazy(() => import('./pages/Admin/AdminRoles'));
const AdminSettings = React.lazy(() => import('./pages/Admin/AdminSettings'));
const WellnessCalendar = React.lazy(() => import('./pages/WellnessCalendar/WellnessCalendar'));
const HRWellnessCalendar = React.lazy(() => import('./pages/HR/HRWellnessCalendar'));

// Public Pages (lazy loaded)
const CorporateRegister = React.lazy(() => import('./pages/CorporateRegister/CorporateRegister'));
const AccountSetup = React.lazy(() => import('./pages/AccountSetup/AccountSetup'));
const ActivateAccount = React.lazy(() => import('./pages/ActivateAccount/ActivateAccount'));
const ContinueAsIndividual = React.lazy(() => import('./pages/ContinueAsIndividual/ContinueAsIndividual'));
const ChoosePlan = React.lazy(() => import('./pages/ChoosePlan/ChoosePlan'));
const Plans = React.lazy(() => import('./pages/Plans/Plans'));
const AdminIndividuals = React.lazy(() => import('./pages/Admin/AdminIndividuals'));
const AdminIndividualClients = React.lazy(() => import('./pages/Admin/AdminIndividualClients'));
const AdminChannelPartners = React.lazy(() => import('./pages/Admin/AdminChannelPartners'));
const AdminChannelPartnerDetail = React.lazy(() => import('./pages/Admin/AdminChannelPartnerDetail'));
const PayLink = React.lazy(() => import('./pages/PayLink/PayLink'));

// Legal Pages (lazy loaded)
const LegalHub = React.lazy(() => import('./pages/Legal/LegalHub'));
const TermsAndConditions = React.lazy(() => import('./pages/Legal/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('./pages/Legal/PrivacyPolicy'));
const CustomerPolicies = React.lazy(
  () => import('./pages/Legal/CustomerPolicies')
);
const AIHealthTerms = React.lazy(
  () => import('./pages/Legal/AIHealthTerms')
);

// Notifications (lazy loaded)
const Notifications = React.lazy(() => import('./pages/Notifications/Notifications'));

// Employee Pages (lazy loaded)
const DiscountVouchers = React.lazy(() => import('./pages/DiscountVouchers/DiscountVouchers'));
const HealthDiscounts = React.lazy(() => import('./pages/HealthDiscounts/HealthDiscounts'));
const Support = React.lazy(() => import('./pages/Support/Support'));
const TicketDetail = React.lazy(() => import('./pages/Support/TicketDetail'));


// Compliance Pages (lazy loaded)
const PrivacyDashboard = React.lazy(() => import('./pages/Privacy/PrivacyDashboard'));
const MFASetup = React.lazy(() => import('./pages/MFA/MFASetup'));
const AdminComplianceDashboard = React.lazy(() => import('./pages/Admin/AdminComplianceDashboard'));
const AdminFeedback = React.lazy(() => import('./pages/Admin/AdminFeedback'));
const TeleConsultationRoom = React.lazy(() => import('./pages/TeleConsultation/VideoRoom'));

// Simple translation helper for non-hook contexts (class components, inline JSX)
const tt = (key: string): string => (i18n as any).t(key);

// Error Boundary
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('App error:', error, info);
    if (error.name === 'ChunkLoadError') {
      window.location.reload();
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center', padding: '40px 20px' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#475569', marginBottom: 12 }}>{tt('common:errors.somethingWentWrong')}</h2>
          <p style={{ color: '#94a3b8', marginBottom: 24 }}>{tt('common:errors.unexpectedError')}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = '/'; }} style={{ background: '#0d9488', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, cursor: 'pointer' }}>{tt('common:errors.goToHome')}</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Layout component for authenticated pages
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isProfileSelected, isViewingAsOther } = useProfile();
  const { user, updateUser, logout } = useAuth();
  const { canAccess: canAccessService } = useServiceAccess();
  const navigate = useNavigate();

  const handleInactivityLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const { showWarning, secondsLeft, stayActive } = useInactivityTimeout({
    onLogout: handleInactivityLogout,
    enabled: !!user,
  });

  const termsVersion =
    require('./content/legal').LEGAL_DOCUMENTS.find((doc: any) => doc.slug === 'terms-and-conditions')?.version ||
    '1.0';
  const privacyVersion =
    require('./content/legal').LEGAL_DOCUMENTS.find((doc: any) => doc.slug === 'privacy-policy')?.version ||
    '1.0';

  const needsConsent =
    user &&
    (!user.accepted_terms ||
      (user.accepted_terms_version && user.accepted_terms_version !== termsVersion) ||
      (user.accepted_privacy_version && user.accepted_privacy_version !== privacyVersion) ||
      (user.accepted_terms && !user.accepted_terms_version) ||
      (user.accepted_terms && !user.accepted_privacy_version));

  const handleConsentAccepted = () => {
    if (user) {
      const updatedUser = {
        ...user,
        accepted_terms: true,
        accepted_terms_version: termsVersion,
        accepted_privacy_version: privacyVersion,
      };
      updateUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  return (
    <ServiceWalletProvider>
      <Header />
      <ProfileBanner />
      {!isProfileSelected && <ProfileSwitcher />}
      <ConsentModal open={!!needsConsent} onAccepted={handleConsentAccepted} />
      <InactivityWarning
        open={showWarning}
        secondsLeft={secondsLeft}
        onStayActive={stayActive}
        onLogout={handleInactivityLogout}
      />
      <main className={`main-content ${isViewingAsOther ? 'has-profile-banner' : ''}`}>
        <AnnouncementMarquee />
        {children}
      </main>
      <Footer />
      <div className={`assistant-shortcuts ${!isViewingAsOther ? 'has-customer-support' : ''}`}>
        {/* Shortcut to the AI Family Doctor (gated like the service itself). */}
        {canAccessService('ai-family-doctor') && <AIChatbot />}
        {!isViewingAsOther && <CustomerSupportChat />}
      </div>
      <QuickAddFAB />
    </ServiceWalletProvider>
  );
};

// Minimal layout for HR/Admin pages — inactivity + consent only, no employee header/FAB
const PortalLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleInactivityLogout = useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const { showWarning, secondsLeft, stayActive } = useInactivityTimeout({
    onLogout: handleInactivityLogout,
    enabled: !!user,
  });

  const termsVersion =
    require('./content/legal').LEGAL_DOCUMENTS.find((doc: any) => doc.slug === 'terms-and-conditions')?.version ||
    '1.0';
  const privacyVersion =
    require('./content/legal').LEGAL_DOCUMENTS.find((doc: any) => doc.slug === 'privacy-policy')?.version ||
    '1.0';

  const needsConsent =
    user &&
    (!user.accepted_terms ||
      (user.accepted_terms_version && user.accepted_terms_version !== termsVersion) ||
      (user.accepted_privacy_version && user.accepted_privacy_version !== privacyVersion) ||
      (user.accepted_terms && !user.accepted_terms_version) ||
      (user.accepted_terms && !user.accepted_privacy_version));

  const handleConsentAccepted = () => {
    if (user) {
      const updatedUser = {
        ...user,
        accepted_terms: true,
        accepted_terms_version: termsVersion,
        accepted_privacy_version: privacyVersion,
      };
      updateUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  useEffect(() => {
    const releaseStuckScrollLock = () => {
      const hasOpenModal = document.querySelector('.MuiModal-root.MuiModal-open');
      const bodyLocked =
        document.body.classList.contains('MuiModal-open') ||
        document.body.style.overflow === 'hidden';

      if (bodyLocked && !hasOpenModal) {
        document.body.classList.remove('MuiModal-open');
        document.body.style.overflow = '';
      }
    };

    releaseStuckScrollLock();
    const interval = window.setInterval(releaseStuckScrollLock, 1500);
    window.addEventListener('resize', releaseStuckScrollLock);
    window.addEventListener('focus', releaseStuckScrollLock);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('resize', releaseStuckScrollLock);
      window.removeEventListener('focus', releaseStuckScrollLock);
    };
  }, []);

  return (
    <>
      <ConsentModal open={!!needsConsent} onAccepted={handleConsentAccepted} />
      <InactivityWarning
        open={showWarning}
        secondsLeft={secondsLeft}
        onStayActive={stayActive}
        onLogout={handleInactivityLogout}
      />
      {children}
    </>
  );
};

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ProfileProvider>
        <ServiceAccessProvider>
        <Router>
          <ScrollToTop />
          <div className="App">
            <MaintenanceGate>
            <Routes>
            {/* Layout guard: blocks any service URL the employee's corporate has
                hidden, including sub-pages and typed/bookmarked links. */}
            <Route element={<ServiceAccessGate />}>
            {/* Public Routes - No Header/Footer */}
            <Route path="/login" element={<Login />} />
            {/* Individuals sign up by choosing a plan (sign-up, email code and payment in one dialog). */}
            <Route path="/register" element={<Navigate to="/choose-plan" replace />} />
            <Route path="/choose-plan" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><ChoosePlan /></React.Suspense>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/register/corporate" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><CorporateRegister /></React.Suspense>} />
            <Route path="/setup-account/:token" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AccountSetup /></React.Suspense>} />
            <Route path="/activate-account" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><ActivateAccount /></React.Suspense>} />
            <Route path="/pay/:token" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><PayLink /></React.Suspense>} />
            <Route path="/continue-as-individual" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><ContinueAsIndividual /></React.Suspense>} />
            <Route path="/legal" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><LegalHub /></React.Suspense>} />
            <Route path="/terms" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><TermsAndConditions /></React.Suspense>} />
            <Route path="/privacy-policy" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><PrivacyPolicy /></React.Suspense>} />
            <Route path="/customer-policies" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><CustomerPolicies /></React.Suspense>} />
            <Route path="/ai-health-terms" element={<React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AIHealthTerms /></React.Suspense>} />

            {/* Role-based home redirect */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <RoleBasedHome />
                </ProtectedRoute>
              }
            />

            {/* Employee Routes - With Header/Footer */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <Home />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/announcements"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <AnnouncementsList />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-records"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <HealthRecords />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/insurance-record"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <InsuranceRecords />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/insurance-records/add"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <InsuranceRecord />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/insurance-records/edit/:id"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <InsuranceRecord />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-assessment"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <HealthAssessment />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/health-summary"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <HealthSummary />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/medicinereminder" element={<Navigate to="/my-reminders" replace />} />
            <Route path="/medicine-reminder" element={<Navigate to="/my-reminders" replace />} />
            <Route
              path="/my-reminders"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <MedicineReminder />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <Profile />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-address-book"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <AddressBook />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><Plans /></React.Suspense>
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-transactions"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <MyPayments />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-methods"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <MyPayments />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gymservices"
              element={
                <ProtectedRoute>
                  <ServiceRoute serviceSlug="gym-services">
                    <AuthenticatedLayout>
                      <GymServices />
                    </AuthenticatedLayout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teleconsultation"
              element={
                <ProtectedRoute>
                  <ServiceRoute serviceSlug="tele-consultation">
                    <AuthenticatedLayout>
                      <TeleConsultation />
                    </AuthenticatedLayout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/teleconsultation/room/:id"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>{tt('common:labels.loading')}</div>}>
                    <TeleConsultationRoom />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/wellness-calendar"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}>
                      <WellnessCalendar />
                    </React.Suspense>
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/child-development"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <ChildDevelopment />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/elderly-care"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <ElderlyCare />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-bookings"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <MyBookings />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <Reminders />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}>
                      <Notifications />
                    </React.Suspense>
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/symptoms-checker"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <SymptomsChecker />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/smart-reports"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <SmartReports />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/disease-prediction"
              element={
                <ProtectedRoute>
                  <ServiceRoute serviceSlug="disease-prediction">
                    <AuthenticatedLayout>
                      <DiseasePrediction />
                    </AuthenticatedLayout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/wearable-devices"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <WearableDevices />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sponsored-packages"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <SponsoredPackages />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers"
              element={
                <ProtectedRoute>
                  <ServiceRoute serviceSlug="discount-vouchers">
                    <AuthenticatedLayout>
                      <React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}>
                        <DiscountVouchers />
                      </React.Suspense>
                    </AuthenticatedLayout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers/vendor/:vendorId"
              element={
                <ProtectedRoute>
                  <ServiceRoute serviceSlug="discount-vouchers">
                    <AuthenticatedLayout>
                      <React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}>
                        <DiscountVouchers />
                      </React.Suspense>
                    </AuthenticatedLayout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/discount-vouchers" element={<Navigate to="/offers" replace />} />
            <Route path="/discount-vouchers/vendor/:vendorId" element={<Navigate to="/offers" replace />} />
            <Route
              path="/discounts/:type"
              element={
                <ProtectedRoute>
                  <ServiceRoute serviceSlug="discount-vouchers">
                    <AuthenticatedLayout>
                      <React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}>
                        <HealthDiscounts />
                      </React.Suspense>
                    </AuthenticatedLayout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            {/* Vendor-first browse: a selected provider is part of the URL so
                the view is shareable and browser back returns to the grid. */}
            <Route
              path="/discounts/:type/vendor/:vendorId"
              element={
                <ProtectedRoute>
                  <ServiceRoute serviceSlug="discount-vouchers">
                    <AuthenticatedLayout>
                      <React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}>
                        <HealthDiscounts />
                      </React.Suspense>
                    </AuthenticatedLayout>
                  </ServiceRoute>
                </ProtectedRoute>
              }
            />
            {/* Support Tickets */}
            <Route path="/support" element={<ProtectedRoute><AuthenticatedLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><Support /></React.Suspense></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/support/:id" element={<ProtectedRoute><AuthenticatedLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><TicketDetail /></React.Suspense></AuthenticatedLayout></ProtectedRoute>} />

            <Route
              path="/womens-health"
              element={
                <ProtectedRoute>
                  <AuthenticatedLayout>
                    <WomensHealth />
                  </AuthenticatedLayout>
                </ProtectedRoute>
              }
            />
            {/* Women's Health Internal Pages */}
            <Route path="/womens-health/cycle-tracker" element={<ProtectedRoute><AuthenticatedLayout><CycleTracker /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/daily-log" element={<ProtectedRoute><AuthenticatedLayout><DailyLog /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/education" element={<ProtectedRoute><AuthenticatedLayout><Education /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/preconception" element={<ProtectedRoute><AuthenticatedLayout><Preconception /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/pregnancy" element={<ProtectedRoute><AuthenticatedLayout><PregnancyDashboard /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/pregnancy-health" element={<ProtectedRoute><AuthenticatedLayout><PregnancyHealth /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/pregnancy-docs" element={<ProtectedRoute><AuthenticatedLayout><PregnancyDocs /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/postpartum-recovery" element={<ProtectedRoute><AuthenticatedLayout><PostpartumRecovery /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/postpartum-mood" element={<ProtectedRoute><AuthenticatedLayout><PostpartumMood /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/feeding" element={<ProtectedRoute><AuthenticatedLayout><Feeding /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/postpartum-fitness" element={<ProtectedRoute><AuthenticatedLayout><PostpartumFitness /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/womens-health/menopause" element={<ProtectedRoute><AuthenticatedLayout><MenopauseHub /></AuthenticatedLayout></ProtectedRoute>} />

            {/* Mental Wellness */}
            <Route path="/ai-family-doctor/:conversationId?" element={<ProtectedRoute><AuthenticatedLayout><AIFamilyDoctor /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/mental-wellness" element={<ProtectedRoute><AuthenticatedLayout><MentalWellness /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/mental-wellness/mood-checkin" element={<ProtectedRoute><AuthenticatedLayout><MoodCheckin /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/mental-wellness/breathing" element={<ProtectedRoute><AuthenticatedLayout><BreathingExercise /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/mental-wellness/screening" element={<ProtectedRoute><AuthenticatedLayout><ScreeningAssessment /></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/mental-wellness/action-plan" element={<ProtectedRoute><AuthenticatedLayout><SelfCareActionPlan /></AuthenticatedLayout></ProtectedRoute>} />

            {/* Employee Mediclaim */}
            <Route path="/my-mediclaim" element={<ProtectedRoute><AuthenticatedLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><EmployeeMediclaim /></React.Suspense></AuthenticatedLayout></ProtectedRoute>} />

            {/* HR Portal Routes */}
            <Route path="/hr" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HRDashboard /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/hr/employees" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HREmployees /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/hr/employees/upload" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HRBulkUpload /></React.Suspense></PortalLayout></RoleRoute>} />

            <Route path="/hr/approvals" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HRApprovals /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/hr/plans" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HRPlans /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/hr/services" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HRServices /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/hr/mediclaim" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HRMediclaim /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/hr/announcements" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HRAnnouncements /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/hr/invoices" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HRInvoices /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/hr/wellness-calendar" element={<RoleRoute allowedRoles={['corporate_admin', 'corporate_hr']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><HRWellnessCalendar /></React.Suspense></PortalLayout></RoleRoute>} />

            {/* Super Admin Routes */}
            <Route path="/admin/corporates" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminCorporates /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/individuals" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminIndividuals /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/clients" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminIndividualClients /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/channel-partners" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminChannelPartners /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/channel-partners/:id" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminChannelPartnerDetail /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/corporates/new" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminCreateCorporate /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/corporates/:id" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminCorporateDetail /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/offers" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminVouchers /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/vouchers" element={<Navigate to="/admin/offers" replace />} />
            <Route path="/admin/vendors/new" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminVendorForm /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/vendors/:id/edit" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminVendorForm /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/packages" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminPackages /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/services" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminPlatformServices /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/billing" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminBilling /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/compliance" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminComplianceDashboard /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/ai-usage" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminAIUsage /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/messaging" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminMessaging /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/gym" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminGymServices /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/teleconsultation" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminTeleConsultation /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/wellness-calendar" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminWellnessCalendar /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/womens-health-content" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminWomensHealthContent /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/elderly-care" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminElderlyCareContent /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/cs-team" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminCSTeam /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/roles" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminRoles /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/settings" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminSettings /></React.Suspense></PortalLayout></RoleRoute>} />
            <Route path="/admin/feedback" element={<RoleRoute allowedRoles={['super_admin']}><PortalLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><AdminFeedback /></React.Suspense></PortalLayout></RoleRoute>} />

            {/* Privacy & Security Routes */}
            <Route path="/privacy" element={<ProtectedRoute><AuthenticatedLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><PrivacyDashboard /></React.Suspense></AuthenticatedLayout></ProtectedRoute>} />
            <Route path="/security/mfa" element={<ProtectedRoute><AuthenticatedLayout><React.Suspense fallback={<div>{tt('common:labels.loading')}</div>}><MFASetup /></React.Suspense></AuthenticatedLayout></ProtectedRoute>} />

            {/* 404 Fallback */}
            <Route path="*" element={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center', padding: '40px 20px' }}>
                <h1 style={{ fontSize: '4rem', fontWeight: 800, color: '#e2e8f0', margin: 0 }}>404</h1>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#475569', margin: '8px 0 16px' }}>{tt('common:errors.pageNotFound')}</h2>
                <p style={{ color: '#94a3b8', marginBottom: 24 }}>{tt('common:errors.pageNotFoundDesc')}</p>
                <a href="/" style={{ color: '#0d9488', fontWeight: 600, textDecoration: 'none' }}>{tt('common:errors.goToHome')}</a>
              </div>
            } />
            </Route>
            </Routes>
            </MaintenanceGate>
          </div>
          <CookieConsent />
        </Router>
        </ServiceAccessProvider>
        </ProfileProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
